// /frontend/src/components/PrecoroSuppliers.js
import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Button, Spin, List, Typography, Tooltip, Input } from 'antd';
import { ArrowLeftOutlined, SearchOutlined, SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { US, CA } from 'country-flag-icons/react/3x2'
import { fetchPrecoroSuppliers, syncPrecoroSuppliers, matchSubcontractorsToPrecoroSuppliers } from '../api';

const PrecoroSuppliers = ({
  open,
  onClose,
  suppliersData,
  onSupplierUpdate,
  messageApi
}) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [suppliers, setSuppliers] = useState(suppliersData);

  const [searchQuery, setSearchQuery] = useState('');
  const filteredSuppliers = useMemo(() => {
    return suppliers?.filter((supplier) => supplier.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [suppliers, searchQuery]);

  const [syncResponse, setSyncResponse] = useState(null);
  const [matchResponse, setMatchResponse] = useState(null);

  useEffect(() => {
    setSuppliers(suppliersData);
  }, [suppliersData]);

  const syncSuppliers = async () => {
    setSyncing(true);
    try {
      const syncResponse = await syncPrecoroSuppliers();
      setSyncResponse(syncResponse);
      onSupplierUpdate();
    } catch (error) {
      console.error('Failed to sync suppliers', error);
      messageApi.error('Failed to sync suppliers');
    } finally {
      setSyncing(false);
    }
  };

  const matchSuppliers = async () => {
    setLoading(true);
    try {
      const matchResponse = await matchSubcontractorsToPrecoroSuppliers();
      setMatchResponse(matchResponse);
      const {data} = await fetchPrecoroSuppliers();
      setSuppliers(data);
      onSupplierUpdate();
    } catch (error) {
      console.error('Failed to match suppliers to subcontractors', error);
      messageApi.error('Failed to match suppliers to subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSyncResponse(null);
    setMatchResponse(null);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      align: 'center',
      sorter: (a, b, sortOrder) => {
        if (a.country === 'Unknown' && b.country === 'Unknown') return sortOrder === 'descend' ? 1 : 0;
        if (a.country === 'Unknown') return sortOrder === 'descend' ? -1 : 1;
        if (b.country === 'Unknown') return sortOrder === 'descend' ? 1 : -1;
        return a.country.localeCompare(b.country);
      },
      sortDirections: ['ascend', 'descend'],
      filters: [
        { text: 'Canada', value: 'Canada' },
        { text: 'USA', value: 'USA' },
        { text: 'Unknown', value: 'Unknown' },
      ],
      onFilter: (value, record) => record.country === value,
      render: (country) => {
        if (country === 'Canada') {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '-12px' }}>
              <CA title="Canada" style={{ width: '24px', height: '16px', marginLeft: '-12px' }} />
            </div>
          );
        } else if (country === 'USA') {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '-12px' }}>
              <US title="United States" style={{ width: '24px', height: '16px', marginLeft: '-12px' }} />
            </div>
          );
        } else {
          return 'Unknown';
        }
      },
    },
    {
      title: 'Subcontractors',
      key: 'subcontractors',
      sorter: (a, b) => {
        const aSubs = a.subcontractors;
        const bSubs = b.subcontractors;

        // If both have exactly one subcontractor, sort by name.
        if (aSubs.length === 1 && bSubs.length === 1) {
          return aSubs[0].name.localeCompare(bSubs[0].name);
        }
        // Otherwise, sort by the length of the subcontractors list.
        return aSubs.length - bSubs.length;
      },
      sortDirections: ['ascend', 'descend'],
      render: (record) => {
        if (record.subcontractors.length === 0) {
          return null;
        } else if (record.subcontractors.length === 1) {
          return record.subcontractors[0].name;
        } else {
          return record.subcontractors.map((sub, index) => (
            <div key={index}>{sub.name}</div>
          ));
        }
      },
    },
  ];

  const renderSupplierNameCountry = (supplier) => {
    const [name, country] = supplier;
    return (
      <div>
        {country === 'Canada' ? (
          <CA title="Canada" style={{ width: '24px', height: '16px', marginRight: '8px' }} />
        ) : country === 'USA' ? (
          <US title="United States" style={{ width: '24px', height: '16px', marginRight: '8px' }} />
        ) : (
          null
        )}
        {name}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      title="Precoro Suppliers"
      onCancel={onClose}
      style={{ top: '10vh' }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      footer={[
        <Button key="sync" type="primary" onClick={syncSuppliers} loading={loading || syncing} disabled={syncing}>
          Sync Suppliers
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={'80%'}
    >
      {loading ? (
        <Spin />
      ) : syncResponse ? (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button
              shape='circle'
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            />
          </div>
          <div style={{ flex: 1, marginLeft: '16px' }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>Sync Results</Typography.Title>
            <Typography.Title level={5}>Added Suppliers</Typography.Title>
            <List
              dataSource={syncResponse.added}
              renderItem={(item) => {
                return (
                  <List.Item>
                    {renderSupplierNameCountry(item)}
                  </List.Item>
                )
              }}
              locale={{ emptyText: 'No suppliers added' }}
            />
            <Typography.Title level={5}>Updated Suppliers</Typography.Title>
            <List
              dataSource={syncResponse.updated}
              renderItem={(item) => {
                return (
                  <List.Item>
                    {renderSupplierNameCountry(item)}
                  </List.Item>
                )
              }}
              locale={{ emptyText: 'No suppliers updated' }}
            />
            <Typography.Title level={5}>Removed Suppliers</Typography.Title>
            <List
              dataSource={syncResponse.removed}
              renderItem={(item) => {
                return (
                  <List.Item>
                    {renderSupplierNameCountry(item)}
                  </List.Item>
                )
              }}
              locale={{ emptyText: 'No suppliers removed' }}
            />
          </div>
        </div>
      ) : matchResponse ? (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button
              shape='circle'
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            />
          </div>
          <div style={{ flex: 1, marginLeft: '16px' }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>Match Results</Typography.Title>
            {matchResponse.matched?.length > 0 && <Typography.Title level={5}>Matched Subcontractors</Typography.Title>}
            <List
              dataSource={matchResponse.matched}
              renderItem={(item) => {
                return (
                  <List.Item>
                    {renderSupplierNameCountry(item)}
                  </List.Item>
                )
              }}
              locale={{ emptyText: 'No new suppliers matched to subcontractors.' }}
            />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Input
              placeholder="Search Suppliers"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <Tooltip
                title={
                  <div style={{ alignContent: 'center'}}>
                    <p>Match Suppliers with Subcontractors by Name.</p>
                    <p>(Name must match exactly.)</p>
                  </div>
                }
              >
                <Button
                  onClick={matchSuppliers}
                  loading={loading}
                >
                  Match Suppliers with Subcontractors <SwapOutlined />
                </Button>
              </Tooltip>
              
              <Tooltip title="Sync Suppliers with Precoro">
                <Button
                  onClick={syncSuppliers}
                  loading={loading}
                >
                  Sync Suppliers with Precoro <SyncOutlined />
                </Button>
              </Tooltip>
            </div>
          </div>
          <Table
            dataSource={filteredSuppliers}
            columns={columns}
            loading={loading || syncing}
            rowKey="id"
            pagination={false}
            scroll={{ y: 'calc(70vh - 90px)' }}
            sticky
            size="small"
          />
        </div>
      )}
    </Modal>
    );
};

export default PrecoroSuppliers;